import * as React from 'react'
import { useContext, useEffect, useState } from 'react'
import SelectComponent, { SelectOption } from '@Components/Selectors/Select'
import Button from '@Components/Buttons/Button'
import ModalPortal from '@Components/Popups/ModalPortal'
import { observer } from 'mobx-react'
import Sidebar from '@Components/Sidebars/Sidebar'
import { StoresContext } from '@App/contexts'
import { createAnnualTsvRequest } from '@App/api/routes/rightsholder/statements'
import ContentHeader from '@Components/ContentHeader'
import RoyaltiesAnnualStatementsTable from '@Scenes/RightsholderScene/RightsholderRoyaltiesAnnualStatements/RoyaltiesAnnualStatementsTable'
import PointPreloader from '@Components/Preloaders/PointPreloader'
import { analyticsSendPage } from '@App/common/utils/analytics-utils'

const RightsholderRoyaltiesAnnualStatements = () => {
    const { statementsStore, rightsholderStore } = useContext(StoresContext)
    const [sidebarIsOpen, setSidebarIsOpen] = useState<boolean>(false)
    const [usagePeriod, setUsagePeriod] = useState<SelectOption>()
    const [loading, setLoading] = useState<boolean>(false)

    const onUsagePeriodChange = async (newUsagePeriod: SelectOption) => {
        setLoading(true)
        setUsagePeriod(newUsagePeriod)
        await statementsStore.loadRoyalties(newUsagePeriod.value)
        setLoading(false)
    }

    const sendTSVRequest = async (payeeId: number) => {
        if (usagePeriod) {
            setSidebarIsOpen(true)
            const rhId = rightsholderStore.currentRightsholderId
            await createAnnualTsvRequest(rhId, { payee: payeeId.toString(), year: usagePeriod.value })
        }
    }

    const usagePeriodOptions = (): SelectOption[] =>
        [2021, 2022, 2023, 2024, 2025]
            .map((year, index) => ({
                label: year.toString(),
                value: year.toString(),
                id: (index + 1).toString(),
            }))
            .reverse()

    useEffect(() => {
        analyticsSendPage('rightsholder_annual_statements')
        setLoading(true)
        statementsStore.clearRoyalties()
        onUsagePeriodChange(usagePeriodOptions()[0]).then()
        return () => {
            statementsStore.clearRoyalties()
        }
    }, [])

    const usagePeriodOptionsArray = usagePeriodOptions()

    return (
        <>
            <title>Royalty Statements</title>
            <div className="royalties-wrapper">
                <ContentHeader
                    title="Annual Statements of Account"
                    descriptionElement={
                        <div>
                            <p>An annual statement is a comprehensive report of royalties for a given year.</p>
                            <p>
                                Select the year you wish to review in the drop-down menu. Once the information
                                populates, you can download the annual statement in the Statement column.
                            </p>
                        </div>
                    }
                />
                <div className="filter-block">
                    <div className="filters-row">
                        <div className="filter-item-wrapper">
                            <div className="filter-item">
                                <p className="small bold">Selected year:</p>
                                <SelectComponent
                                    options={usagePeriodOptionsArray}
                                    selectedValue={usagePeriod}
                                    defaultValue={usagePeriodOptionsArray[0]}
                                    onValueChange={onUsagePeriodChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {loading ? <PointPreloader /> : <RoyaltiesAnnualStatementsTable sendTSVRequest={sendTSVRequest} />}
            </div>

            <ModalPortal isOpen={sidebarIsOpen} onClose={() => setSidebarIsOpen(false)}>
                <Sidebar onClose={() => setSidebarIsOpen(false)}>
                    <div className="download-sidebar">
                        <h2>Your files are on the way!</h2>
                        <p className="large">
                            We&apos;re preparing your files for download. You&apos;ll receive an email shortly with
                            downloadable statements.
                        </p>
                        <div className="download-button">
                            <Button mainColor type="primary" onClick={() => setSidebarIsOpen(false)}>
                                Done
                            </Button>
                        </div>
                    </div>
                </Sidebar>
            </ModalPortal>
        </>
    )
}

export default observer(RightsholderRoyaltiesAnnualStatements)
